const STORAGE_KEY = "hamazon.adminCouponBatchAttempts";

let memoryAttempts = [];

const customerRef = (customer) =>
  `${String(customer?.customerId ?? "").trim()}\0${String(customer?.email ?? "")
    .trim()
    .toLowerCase()}`;

const sameOffer = (body, base) =>
  body?.branch === base.branch &&
  body?.sum === base.sum &&
  body?.minOrder === base.minOrder &&
  body?.expiredAt === base.expiredAt;

const isCustomer = (value) =>
  Boolean(value) &&
  typeof value.customerId === "string" &&
  value.customerId.trim() !== "" &&
  typeof value.email === "string" &&
  value.email.trim() !== "";

const isAttempt = (value) =>
  Boolean(value) &&
  typeof value.idempotencyKey === "string" &&
  value.idempotencyKey.trim() !== "" &&
  typeof value.settled === "boolean" &&
  typeof value.body?.branch === "string" &&
  typeof value.body?.sum === "number" &&
  typeof value.body?.minOrder === "number" &&
  typeof value.body?.expiredAt === "string" &&
  Array.isArray(value.body?.customers) &&
  value.body.customers.every(isCustomer) &&
  Array.isArray(value.created) &&
  Array.isArray(value.failed);

function browserStorage() {
  try {
    if (typeof sessionStorage === "undefined") return null;
    return sessionStorage;
  } catch {
    return null;
  }
}

export function readCouponBatchAttempts() {
  const storage = browserStorage();
  if (!storage) return memoryAttempts.slice();
  try {
    const parsed = JSON.parse(storage.getItem(STORAGE_KEY) || "[]");
    const attempts = Array.isArray(parsed) ? parsed.filter(isAttempt) : [];
    memoryAttempts = attempts;
    return attempts.slice();
  } catch {
    return memoryAttempts.slice();
  }
}

export function writeCouponBatchAttempts(attempts) {
  memoryAttempts = attempts.slice();
  const storage = browserStorage();
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(memoryAttempts));
  } catch {
    // Keep the in-memory copy when sessionStorage is full or blocked.
  }
}

/**
 * Split this submit into CPS calls.
 * A chunk that never got a response keeps its Idempotency-Key and its exact body.
 * Customers already present in a settled `created` list are not sent again.
 * Everyone else is a new chunk with a new key.
 */
export function planCouponBatchSends({
  base,
  customers,
  attempts,
  createKey,
  chunkSize,
}) {
  const selected = new Map(
    customers.map((customer) => [customerRef(customer), customer]),
  );
  const claimed = new Set();
  const alreadyCreated = [];
  const nextAttempts = attempts.slice();

  for (const attempt of nextAttempts) {
    if (!attempt.settled || !sameOffer(attempt.body, base)) continue;
    for (const coupon of attempt.created) {
      const ref = customerRef(coupon);
      if (!selected.has(ref) || claimed.has(ref)) continue;
      claimed.add(ref);
      alreadyCreated.push(coupon);
    }
  }

  const sends = [];

  for (const attempt of nextAttempts) {
    if (attempt.settled || !sameOffer(attempt.body, base)) continue;
    const chunkCustomers = attempt.body.customers;
    const overlapsSelection = chunkCustomers.some((customer) =>
      selected.has(customerRef(customer)),
    );
    if (!overlapsSelection) continue;
    for (const customer of chunkCustomers) claimed.add(customerRef(customer));
    sends.push({
      body: attempt.body,
      idempotencyKey: attempt.idempotencyKey,
      resumed: true,
    });
  }

  const remaining = customers.filter(
    (customer) => !claimed.has(customerRef(customer)),
  );
  for (let i = 0; i < remaining.length; i += chunkSize) {
    const body = {
      branch: base.branch,
      sum: base.sum,
      minOrder: base.minOrder,
      expiredAt: base.expiredAt,
      customers: remaining.slice(i, i + chunkSize),
    };
    const idempotencyKey = createKey();
    nextAttempts.push({
      idempotencyKey,
      settled: false,
      body,
      created: [],
      failed: [],
    });
    sends.push({ body, idempotencyKey, resumed: false });
  }

  return { alreadyCreated, sends, attempts: nextAttempts };
}

export function settleCouponBatchAttempt(attempts, idempotencyKey, result) {
  return attempts.map((attempt) =>
    attempt.idempotencyKey === idempotencyKey
      ? {
          ...attempt,
          settled: true,
          created: result.created,
          failed: result.failed,
        }
      : attempt,
  );
}

/**
 * Customers in chunks that were never requested.
 * `stoppedIndex` is the chunk whose request threw; later chunks were not sent.
 */
export function customersNotSent(sends, stoppedIndex) {
  if (
    !Array.isArray(sends) ||
    !Number.isInteger(stoppedIndex) ||
    stoppedIndex < 0
  ) {
    return [];
  }
  return sends
    .slice(stoppedIndex + 1)
    .flatMap((send) =>
      Array.isArray(send?.body?.customers) ? send.body.customers : [],
    );
}

/** Explains that later chunks were not sent, and that the same selection can be sent again. */
export function unsentBatchNotice(customerCount, chunkCount) {
  if (customerCount <= 0 || chunkCount <= 0) return "";
  const people =
    customerCount === 1
      ? "לקוח אחד לא נשלח"
      : `${customerCount} לקוחות לא נשלחו`;
  const chunks =
    chunkCount === 1
      ? "המקטע הבא לא נשלח בכלל"
      : `${chunkCount} המקטעים הבאים לא נשלחו בכלל`;
  return `השליחה נעצרה באמצע. ${chunks}, ולכן ${people}. שליחה חוזרת של אותה בחירה תשלח אותם. קופון שכבר נוצר לא ייווצר שוב.`;
}

/** Drop finished attempts for this offer so a later identical submit is a new batch. */
export function releaseSettledCouponBatchOffer(attempts, base) {
  return attempts.filter(
    (attempt) => !(attempt.settled && sameOffer(attempt.body, base)),
  );
}
